import { DownloadAppBanner } from "@/components/download-app-banner";
import { AgencySection } from "@/components/trip/agency-section";
import { CancellationSection } from "@/components/trip/cancellation-section";
import { DescriptionSection } from "@/components/trip/description-section";
import { DetailsSection } from "@/components/trip/details-section";
import { HeroImage } from "@/components/trip/hero-image";
import { PricingSection } from "@/components/trip/pricing-section";
import { ReservationSection } from "@/components/trip/reservation-section";
import { TagsSection } from "@/components/trip/tags-section";
import { TitleSection } from "@/components/trip/title-section";
import { orpc } from "@/utils/clients/orpc";
import { Button, Divider, addToast } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Share2 } from "lucide-react";

export const Route = createFileRoute("/trips/$id/")({
  component: Component,
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(orpc.trip.get.queryOptions({ input: { id: params.id } }));
  },
  errorComponent: TripError,
  notFoundComponent: TripNotFound,
});

function Component() {
  const { id } = Route.useParams();

  const { data: trip, isLoading, error } = useQuery(orpc.trip.get.queryOptions({ input: { id } }));

  if (error && !trip) {
    return <TripError error={error} />;
  }

  if (!trip && !isLoading) {
    return <TripNotFound />;
  }

  const handleShare = async () => {
    if (!trip) return;
    const shareData = {
      title: trip.name,
      text: `Check out ${trip.name} by ${trip.agency.name} on Rehla`,
      url: window.location.href,
    };
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(window.location.href);
      addToast({
        title: "Link copied to clipboard",
      });
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <HeroImage imageKeys={trip?.imageKeys} isLoading={isLoading} />

      <div className="mx-auto -mt-12 max-w-2xl px-4 pb-28 sm:px-6">
        <div className="relative flex flex-col gap-6">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <TitleSection trip={trip} isLoading={isLoading} />
            </div>
            {trip ? (
              <Button isIconOnly variant="flat" size="sm" className="mt-1 ml-3 shrink-0" onPress={handleShare}>
                <Share2 className="size-4" />
              </Button>
            ) : null}
          </div>
          <Divider />
          <DetailsSection trip={trip} isLoading={isLoading} />
          <TagsSection trip={trip} isLoading={isLoading} />
          <DescriptionSection trip={trip} isLoading={isLoading} />
          <Divider />
          <PricingSection trip={trip} isLoading={isLoading} />
          <Divider />
          <ReservationSection trip={trip} isLoading={isLoading} />
          {trip ? (
            <CancellationSection
              cancellationDeadline={trip.cancellationDeadline}
              adultCancellationFee={trip.adultCancellationFee}
              childCancellationFee={trip.childCancellationFee}
              hasChildPricing={trip.childMaximumAge > 0}
              isLoading={false}
            />
          ) : (
            <CancellationSection
              cancellationDeadline={null}
              adultCancellationFee={null}
              childCancellationFee={null}
              hasChildPricing={false}
              isLoading={isLoading}
            />
          )}
          <Divider />
          <AgencySection agency={trip ? { id: trip.agencyId, ...trip.agency } : undefined} isLoading={isLoading} />
        </div>
      </div>
      <DownloadAppBanner />
    </div>
  );
}

function TripError({ error }: { error?: unknown }) {
  const message = error instanceof Error ? error.message : "Failed to load trip";
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
      <AlertTriangle className="text-danger size-12" />
      <h1 className="text-danger text-xl font-bold">Something went wrong</h1>
      <p className="text-default-500 text-center">{message}</p>
      <Button variant="flat" startContent={<ArrowLeft className="size-4" />} onPress={() => window.history.back()}>
        Go back
      </Button>
    </div>
  );
}

function TripNotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
      <h1 className="text-xl font-bold">Trip not found</h1>
      <p className="text-default-500 text-center">The trip you're looking for doesn't exist or has been removed.</p>
      <Button variant="flat" startContent={<ArrowLeft className="size-4" />} onPress={() => window.history.back()}>
        Go back
      </Button>
    </div>
  );
}
